import { useState, type FC } from 'react';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { Box, Typography } from '@mui/material';
import type { ILoginFormProps } from '@components/ui/LoginForm/interface';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from './translations.json';
import SignInForm from './SignInForm';
import VerifyEmailForm from './VerifyEmailForm';

type SupportedLanguage = keyof typeof translations;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const message = value.trim();
    if (!message) return undefined;

    try {
      return getErrorMessage(JSON.parse(message)) || value;
    } catch {
      return value;
    }
  }

  if (!isRecord(value)) return undefined;

  if (Array.isArray(value.violations)) {
    const messages = value.violations
      .filter(isRecord)
      .map((violation) => violation.message)
      .filter((message): message is string => typeof message === 'string' && Boolean(message.trim()));

    if (messages.length) return messages.join('\n');
  }

  return getErrorMessage(value.message) || getErrorMessage(value.detail);
};

const LoginForm: FC<ILoginFormProps> = ({ onSubmit, onVerify }) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = async (action: () => Promise<void>) => {
    setLoading(true);
    setError(null);

    try {
      await action();
    } catch (err: unknown) {
      const isAxiosError = axios.isAxiosError<unknown>(err);
      const status = isAxiosError ? err.response?.status : undefined;
      const responseMessage = isAxiosError ? getErrorMessage(err.response?.data) : undefined;

      if (status === 401) {
        setError(content.invalidCredentials);
      } else if (status === 500) {
        setError(content.serverError);
      } else {
        setError(responseMessage || getErrorMessage(err instanceof Error ? err.message : undefined) || content.genericError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = () => runAction(async () => {
    const response: any = await onSubmit({ email });
    if (response?.success) {
      enqueueSnackbar(content.codeSent, { variant: 'success' });
      setStep(2);
    } else {
      setError(content.codeSendError);
    }
  });

  const handleVerify = () => runAction(() => onVerify({ email, code }));

  const handleBack = () => {
    setStep(1);
    setCode('');
    setError(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '440px',
        backgroundColor: '#171d25',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '12px',
        p: { xs: 3, sm: 4 },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[content.stepLogin, content.stepVerification].map((label, index) => {
          const stepNumber = index + 1;
          const isActive = step === stepNumber;

          return (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#ffffff',
                  backgroundColor: step >= stepNumber ? '#1976d2' : 'rgba(255, 255, 255, 0.12)',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {stepNumber}
              </Box>
              <Typography
                variant="caption"
                sx={{ color: isActive ? '#ffffff' : '#94a3b8', fontWeight: isActive ? 600 : 400 }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {step === 1 ? (
        <SignInForm
          content={content}
          email={email}
          error={error}
          loading={loading}
          onEmailChange={setEmail}
          onSubmit={handleSignIn}
        />
      ) : (
        <VerifyEmailForm
          content={content}
          email={email}
          code={code}
          error={error}
          loading={loading}
          onBack={handleBack}
          onCodeChange={setCode}
          onSubmit={handleVerify}
        />
      )}
    </Box>
  );
};

export default LoginForm;
